/**
 * UploadedFileIndicators Component
 *
 * Container component that displays uploaded file indicators in the chat input area.
 * Shows files that have been successfully uploaded and are ready to be sent with the next message.
 *
 * Features:
 * - Displays uploaded files from conversation state
 * - Horizontal layout with wrapping
 * - Individual file items with remove functionality
 * - Automatically hides when no uploaded files exist
 */

import React from 'react';
import { Box } from '@mantine/core';
import { useShallow } from 'zustand/react/shallow';
import { useChatStore } from '../../../../../stores/chatStore';
import { UploadedFileItem } from '../UploadedFileItem';
import type { UploadedFileIndicatorsProps } from './types';
import classes from './UploadedFileIndicators.module.css';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UploadedFileIndicators: React.FC<UploadedFileIndicatorsProps> = ({
  conversationId,
  onRemove,
  className,
}) => {
  // =============================================================================
  // STATE
  // =============================================================================

  // Get pending files for next message from chat store (not all conversation files)
  const pendingFiles = useChatStore(
    useShallow((state) => state.getConversationFiles(conversationId))
  );
  const getFileEntry = useChatStore((state) => state.getFileEntry);

  // =============================================================================
  // RENDER
  // =============================================================================

  // Don't render anything if no pending files
  if (!pendingFiles.length) {
    return null;
  }

  return (
    <Box className={`${classes.container} ${className || ''}`}>
      {pendingFiles.map((filename) => {
        const entry = getFileEntry?.(conversationId, filename);
        const isUploading = !!entry && (entry.status === 'queued' || entry.status === 'uploading');
        const isDeleting = !!entry && entry.status === 'deleting';
        return (
          <UploadedFileItem
            key={filename}
            filename={filename}
            conversationId={conversationId}
            onRemove={!isUploading && !isDeleting ? onRemove : undefined}
            isUploading={isUploading}
            isDeleting={isDeleting}
            compact={true}
          />
        );
      })}
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default UploadedFileIndicators;
